/**
 * SectionNav Component — Case Associates LP Override
 *
 * Overrides the base landing-page SectionNav to use bg-secondary background
 * with a primary-colored active section underline.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  useContentToken,
  EditableToken,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';

export const contentSchema: ComponentContentSchema = {
  slot: 'Navigation',
  displayName: 'Section Navigation',
  tokens: {
    'navigation.overview_label': {
      label: 'Overview Label',
      type: 'text',
      description: 'Navigation link text for the Overview section',
      defaultValue: 'Overview',
    },
    'navigation.floorplans_label': {
      label: 'Floor Plans Label',
      type: 'text',
      description: 'Navigation link text for the Floor Plans section',
      defaultValue: 'Floor Plans',
    },
    'navigation.amenities_label': {
      label: 'Amenities Label',
      type: 'text',
      description: 'Navigation link text for the Amenities section',
      defaultValue: 'Amenities',
    },
    'navigation.neighborhood_label': {
      label: 'Neighborhood Label',
      type: 'text',
      description: 'Navigation link text for the Neighborhood section',
      defaultValue: 'Neighborhood',
    },
  },
};

const SECTIONS = ['overview', 'floorplans', 'amenities', 'neighborhood'] as const;

export function SectionNav() {
  const overviewLabel = useContentToken('navigation.overview_label');
  const floorplansLabel = useContentToken('navigation.floorplans_label');
  const amenitiesLabel = useContentToken('navigation.amenities_label');
  const neighborhoodLabel = useContentToken('navigation.neighborhood_label');

  const [activeSection, setActiveSection] = useState<string>('overview');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { threshold: 0.3, rootMargin: '-80px 0px -50% 0px' }
    );

    for (const id of SECTIONS) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const linkClass = (id: string) =>
    `block w-full md:w-auto py-3 md:py-4 text-secondary-foreground no-underline text-center text-sm md:text-base font-medium font-accent transition-all duration-200 border-b-[3px] whitespace-normal px-4 min-w-0 ${
      activeSection === id
        ? 'border-b-primary opacity-100'
        : 'border-transparent opacity-70 hover:opacity-100'
    }`;

  return (
    <nav className="bg-secondary sticky top-0 z-[900] border-secondary-foreground/10 overflow-x-hidden font-body">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 flex flex-col md:flex-row md:flex-wrap md:justify-center md:items-center gap-0 md:gap-x-6 lg:gap-x-12">
        <a href="#overview" onClick={(e) => handleClick(e, 'overview')} className={linkClass('overview')}>
          <EditableToken path="navigation.overview_label" type="text" label="Overview Label" as="span">
            {overviewLabel}
          </EditableToken>
        </a>
        <a href="#floorplans" onClick={(e) => handleClick(e, 'floorplans')} className={linkClass('floorplans')}>
          <EditableToken path="navigation.floorplans_label" type="text" label="Floor Plans Label" as="span">
            {floorplansLabel}
          </EditableToken>
        </a>
        <a href="#amenities" onClick={(e) => handleClick(e, 'amenities')} className={linkClass('amenities')}>
          <EditableToken path="navigation.amenities_label" type="text" label="Amenities Label" as="span">
            {amenitiesLabel}
          </EditableToken>
        </a>
        <a href="#neighborhood" onClick={(e) => handleClick(e, 'neighborhood')} className={linkClass('neighborhood')}>
          <EditableToken path="navigation.neighborhood_label" type="text" label="Neighborhood Label" as="span">
            {neighborhoodLabel}
          </EditableToken>
        </a>
      </div>
    </nav>
  );
}

(SectionNav as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SectionNav;
